/**
 * Host Control Panel (design 2c) — drives the presentation view over
 * Socket.io. Left: quiz outline. Center stage: slide preview + ANSWER/UP-NEXT
 * cards + control dock (Prev / Next / Reveal-on-projector / Standings).
 * Answers are hidden behind hold-A-to-peek when privacy mode is on.
 */
import { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Theme, Button, IconButton, TextField } from '@radix-ui/themes';
import { io, Socket } from 'socket.io-client';
import api from '../../utils/api';
import { socketAuth } from '../../utils/authToken';
import config from '../../config';
import { useSiteConfig } from '../../utils/siteConfig';
import { songAnswerText } from '../../utils/songAnswer';
import { choiceLetter, isMultipleChoice } from '../../utils/multipleChoice';
import { missingChoiceKeys, missingChoiceLabel } from '../../utils/preflight';
import { DRAWING_SPIN_MS, drawingBlockedReason } from '../../utils/drawing';
import {
  buildSlideSequence as buildSequence,
  findSequenceIndex,
  mergeCustomSlides,
  stepSequence,
  type CustomSlide,
  type SeqEntry,
  type SlideType,
} from '../../utils/slideSequence';
import { customSlideLabel, placementLabel } from '../../utils/customSlides';
import { CurrentRoundLockButton, RoundLocksView, useRoundLocks } from '../AnswerReview/RoundLocks';
import './HostPanel.css';

// The current question renders as a heading in the preview card. A long
// question would overflow and make the preview column scroll; instead shrink
// the font until the whole preview fits without scrolling (down to a floor).
// Re-fits on every render (answer reveal changes the layout) and on resize.
function FitHeading({ text }: { text: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const box = el?.closest('.host-preview') as HTMLElement | null;
    if (!el || !box) return;
    const fit = () => {
      let size = 1.5; // rem — matches .preview-content h2
      el.style.fontSize = `${size}rem`;
      let guard = 0;
      while (box.scrollHeight > box.clientHeight + 1 && size > 0.8 && guard < 30) {
        size -= 0.05;
        el.style.fontSize = `${size}rem`;
        guard += 1;
      }
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  });
  return <h2 ref={ref}>{text}</h2>;
}

type QuestionType = 'TEXT' | 'SONG' | 'IMAGE';

interface Question {
  id: string;
  orderIndex: number;
  type: QuestionType;
  text: string;
  answer: string;
  answerPartB?: string;
  notes?: string;
  // Song-question fields (only meaningful when type === 'SONG')
  songTitle?: string;
  songArtist?: string;
  songYoutubeUrl?: string;
  songStartTime?: string;
  songDuration?: number;
  audioPath?: string;
  // Own point value. null = the round default.
  points?: number | null;
  // Multiple choice: 2 to 6 choices, and the 0-based index of the correct
  // one. The index goes to the projector only with a reveal.
  choices?: string[];
  correctChoice?: number | null;
}

// Projector fields of a multiple choice question BEFORE the reveal: the
// choice texts only, never the correct index.
function choicesForQuestion(q: Question | undefined): Record<string, unknown> {
  return q && isMultipleChoice(q) ? { choices: q.choices } : {};
}

// Projector fields of a multiple choice question AT the reveal: the choice
// texts and the correct index.
function choicesForReveal(q: Question | undefined): Record<string, unknown> {
  return q && isMultipleChoice(q) ? { choices: q.choices, correctChoice: q.correctChoice ?? null } : {};
}

// A question's own value, when it differs from the round value. The
// projector shows it as a small "3 pts" tag. null = no tag.
function pointsTag(q: Question | undefined, round: Round | undefined): number | null {
  if (!q || q.points == null || !round || round.roundType === 'VISUAL') return null;
  const roundPoints = round.pointsPerQuestion ?? 2;
  return q.points === roundPoints ? null : q.points;
}

interface Round {
  id: string;
  orderIndex: number;
  title: string;
  // roundType is a constraint on question types this round may hold.
  // Song data lives on each SONG-type question, not on the round.
  roundType: 'STANDARD' | 'VISUAL' | 'AUDIO';
  twoAnswerMode: boolean;
  pointsPerQuestion?: number;
  questions: Question[];
}

interface EventData {
  id: string;
  pin: string;
  adminPin: string;
  status: string;
  // Display-name override; falls back to quiz.name when null.
  name?: string | null;
  venue: { name: string };
  quiz: {
    id: string;
    name: string;
    // Welcome / rules content. Drives the WELCOME, RULES, and
    // ROUNDS_OVERVIEW slides. NULL/empty rulesText suppresses the
    // rules slide from the sequence.
    welcomePrizes: string | null;
    welcomeStartTime: string | null;
    rulesText: string | null;
    // Optional-slide configuration. Defaults preserve the historical
    // behavior (drawing + outro always on; drawing inserts before
    // final standings).
    showDrawingSlide: boolean;
    drawingAfterRound: number | null;
    showOutroSlide: boolean;
    standingsHighlightCount: number;
    rounds: Round[];
  };
  // Resolved welcome content from the backend: liveEvent override -> venue
  // default -> quiz legacy. Optional so older payloads still type-check; the
  // quiz.* fields are the fallback when this is absent.
  welcome?: {
    prizes: string | null;
    rulesText: string | null;
    startTime: string | null;
  } | null;
  // Host-made slides, resolved by the server to a slot in the sequence.
  customSlides?: CustomSlide[];
}

interface PresentationState {
  currentRound: number;  // index into rounds array
  currentQuestion: number;  // index into questions array
  answerRevealed: boolean;
  showingStandings: boolean;
  slideType: SlideType;
  // For ROUND_SUMMARY: how many questions have been revealed so far.
  // Starts at 0 when the slide opens; each Next click reveals one more.
  // Once it equals the round's question count, the next Next advances
  // to the following slide.
  summaryVisibleCount: number;
  // For ROUND_SUMMARY: how many answers have been revealed so far. Stays
  // 0 until the host hits Show Answers (PIN-gated), which reveals the
  // first answer; thereafter each Next reveals one more answer until all
  // are shown, then Next advances to the following slide. answerRevealed
  // is true whenever this is > 0.
  summaryAnswerCount: number;
  // For CUSTOM: the placement id of the slide on screen. Null otherwise.
  customSlideId: string | null;
}

const HostPanel: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const brandName = useSiteConfig().get('brandName', 'West Slope Quiz');
  const [event, setEvent] = useState<EventData | null>(null);
  // Custom slides as the server last sent them (on load, then on each
  // 'slides-updated' push). Kept apart from `event` because a new `event`
  // object reconnects the socket.
  const [serverSlides, setServerSlides] = useState<CustomSlide[]>([]);
  // The list the sequence uses. It equals serverSlides, except that a slide
  // removed or moved while on screen stays at its old place until the
  // host leaves it (see mergeCustomSlides).
  const [slidesView, setSlidesView] = useState<CustomSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adminPinInput, setAdminPinInput] = useState('');
  const [showPinPrompt, setShowPinPrompt] = useState(false);
  const [expandedRounds, setExpandedRounds] = useState<Set<number>>(new Set());
  // Round sheet modal — full-list of the current round's questions
  // with answers so the host can read them without turning toward the
  // projector. Stores the round index so the modal can preselect a
  // round; the host can switch rounds within the modal.
  const [showRoundSheet, setShowRoundSheet] = useState(false);
  const [roundSheetRound, setRoundSheetRound] = useState<number | null>(null);
  const socketRef = useRef<Socket | null>(null);
  // Connection state for the host's own socket. While disconnected we
  // disable nav/show-answer so the host doesn't fire blind clicks into
  // the void and find out by the room yelling at the screen.
  const [hostConnected, setHostConnected] = useState(true);
  // Banner when the audience's audio element reports an error — the
  // host needs to know to advance manually instead of waiting.
  const [audioErrorMsg, setAudioErrorMsg] = useState<string | null>(null);
  // False when the server says this socket may not drive the projector
  // (for example, the sign-in expired and a fresh token did not help).
  // The server drops slide changes from such a socket without a reply,
  // so the host must see a warning.
  const [hostAuthorized, setHostAuthorized] = useState(true);
  // Random drawing as seen from the host side. Mirrors the audience screen:
  // PLAY_DRAWING (from the host's Draw button or the one on the TV) starts
  // the spin, and the winner shows here once the TV reveals it.
  const [drawStatus, setDrawStatus] = useState<'idle' | 'picking' | 'spinning' | 'revealed'>('idle');
  const [drawWinner, setDrawWinner] = useState('');
  const [drawError, setDrawError] = useState('');
  const drawRevealTimerRef = useRef<number | null>(null);
  // Read by the no-reply timeout, which must not see a stale status.
  const drawStatusRef = useRef(drawStatus);
  drawStatusRef.current = drawStatus;

  // Privacy mode (default ON, persisted per host in localStorage). When on,
  // every answer on the host's own screen is obscured behind blur; the host
  // reveals it locally only while holding A (or the peek button). Projecting
  // to the audience is a separate, PIN-gated action. Nothing readable if
  // someone walks behind the host.
  const [privacyMode, setPrivacyMode] = useState<boolean>(() => {
    try { return localStorage.getItem('host:privacyMode') !== 'off'; }
    catch { return true; }
  });
  const togglePrivacy = useCallback(() => {
    setPrivacyMode((p) => {
      const next = !p;
      try { localStorage.setItem('host:privacyMode', next ? 'on' : 'off'); }
      catch { /* storage disabled — ignore */ }
      return next;
    });
  }, []);
  // Held-key peek state: true only while A (or the peek button) is held down.
  const [peeking, setPeeking] = useState(false);
  // Reminder popover open state (top-strip control).
  const [reminderOpen, setReminderOpen] = useState(false);

  // Stopwatch state — counts up from 0 every time the current slide
  // changes, so the host can see how long they've been parked on a
  // question. Host-only; not broadcast to the audience.
  const [slideEnteredAt, setSlideEnteredAt] = useState<number>(() => Date.now());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Per-round summary-slide font scale (1.0 = auto). Lets the host
  // nudge sizing live for rounds whose answer list doesn't quite fit
  // the auto-sizer's choice. Persisted to localStorage keyed by quiz
  // id so a refresh mid-show preserves the host's tuning.
  const [summaryScaleByRound, setSummaryScaleByRound] = useState<
    Record<number, number>
  >({});

  // Slide-preview zoom (1 = default). Lets the host enlarge the center
  // preview card to read it from across the room. Persisted per quiz so
  // a mid-show refresh keeps the host's chosen size.
  const PREVIEW_SCALE_MIN = 0.8;
  const PREVIEW_SCALE_MAX = 1.8;
  const PREVIEW_SCALE_STEP = 0.1;
  const [previewScale, setPreviewScale] = useState(1);
  const adjustPreviewScale = useCallback((delta: number) => {
    setPreviewScale((s) => {
      const next = Math.round((s + delta) * 10) / 10;
      return Math.min(PREVIEW_SCALE_MAX, Math.max(PREVIEW_SCALE_MIN, next));
    });
  }, []);

  const [state, setState] = useState<PresentationState>({
    currentRound: 0,
    currentQuestion: 0,
    answerRevealed: false,
    showingStandings: false,
    summaryVisibleCount: 0,
    summaryAnswerCount: 0,
    slideType: 'WELCOME',
    customSlideId: null,
  });

  // Per-item hide toggles. A hidden item is skipped by Next/Prev and
  // dimmed in the outline. Round-level hide implicitly hides every child.
  // Keys: 'round-<ri>' for a whole round, or '<slideType>-<ri>(-<qi>)'
  // for an individual sub-item.
  const [hiddenItems, setHiddenItems] = useState<Set<string>>(new Set());

  const toggleHidden = useCallback((key: string) => {
    setHiddenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // itemKey and isItemHidden are declared further down — they depend on
  // `rounds` which is derived from event state below.

  // Reminder banner state. reminderActive mirrors the audience-side
  // banner (synced via the SHOW_REMINDER replay on connect) so a host
  // who reloads doesn't lose the ability to dismiss.
  const [reminderInput, setReminderInput] = useState('');
  const [reminderActive, setReminderActive] = useState(false);

  const sendReminder = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setReminderInput(trimmed);
    setReminderActive(true);
    if (socketRef.current && event) {
      socketRef.current.emit('presentation-control', {
        pin: event.pin,
        action: 'SHOW_REMINDER',
        payload: { text: trimmed },
      });
    }
  }, [event]);

  const clearReminder = useCallback(() => {
    setReminderActive(false);
    if (socketRef.current && event) {
      socketRef.current.emit('presentation-control', {
        pin: event.pin,
        action: 'CLEAR_REMINDER',
        payload: {},
      });
    }
  }, [event]);

  // Common presets — the buttons next to the input. Edit this list to
  // change which one-click reminders the host gets.
  const REMINDER_PRESETS = [
    'Make sure your team name is on your card',
    'Turn in rounds 5 & 6 now',
    'Last call for round answers',
    'Visual round due with round 5',
  ];

  // Load event data
  useEffect(() => {
    const loadEvent = async () => {
      try {
        const res = await api.get(`/api/events/${id}`);
        setEvent(res.data.data.event);
        setServerSlides(res.data.data.event.customSlides ?? []);
      } catch (err) {
        setError('Failed to load event');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadEvent();
  }, [id]);

  // Load + persist the per-round summary scale to localStorage, keyed
  // by quiz id so multiple quizzes don't stomp on each other. The host
  // can refresh mid-show without losing their text-fit tuning.
  const quizId = event?.quiz?.id ?? null;
  const summaryScaleStorageKey = quizId ? `host:summaryScale:${quizId}` : null;
  useEffect(() => {
    if (!summaryScaleStorageKey) return;
    try {
      const raw = localStorage.getItem(summaryScaleStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setSummaryScaleByRound(parsed as Record<number, number>);
        }
      }
    } catch { /* localStorage unavailable or malformed JSON — ignore */ }
  }, [summaryScaleStorageKey]);
  useEffect(() => {
    if (!summaryScaleStorageKey) return;
    try {
      localStorage.setItem(summaryScaleStorageKey, JSON.stringify(summaryScaleByRound));
    } catch { /* quota exceeded or storage disabled — ignore */ }
  }, [summaryScaleByRound, summaryScaleStorageKey]);

  const getSummaryScale = useCallback(
    (roundIdx: number): number => summaryScaleByRound[roundIdx] ?? 1,
    [summaryScaleByRound],
  );

  // Load + persist the preview zoom, keyed by quiz like the summary scale.
  const previewScaleStorageKey = quizId ? `host:previewScale:${quizId}` : null;
  useEffect(() => {
    if (!previewScaleStorageKey) return;
    try {
      const raw = localStorage.getItem(previewScaleStorageKey);
      const parsed = raw ? Number(raw) : NaN;
      if (Number.isFinite(parsed)) {
        setPreviewScale(
          Math.min(PREVIEW_SCALE_MAX, Math.max(PREVIEW_SCALE_MIN, parsed)),
        );
      }
    } catch { /* localStorage unavailable or malformed — ignore */ }
  }, [previewScaleStorageKey]);
  useEffect(() => {
    if (!previewScaleStorageKey) return;
    try {
      localStorage.setItem(previewScaleStorageKey, String(previewScale));
    } catch { /* quota exceeded or storage disabled — ignore */ }
  }, [previewScale, previewScaleStorageKey]);

  // Connect to Socket.io
  useEffect(() => {
    if (!event) return;

    // Connect to the origin (not config.apiBaseUrl) — the trailing /api on
    // the API URL would otherwise be treated as a namespace and our server
    // only listens on the default namespace. See PresentationView for the
    // matching change on the audience side.
    const socketOrigin = (() => {
      try { return new URL(config.apiBaseUrl).origin; }
      catch { return config.apiBaseUrl; }
    })();
    // `auth` is a function, so socket.io asks for a fresh Clerk token on
    // every connection attempt, reconnects included. The legacy JWT cookie
    // rides along on the handshake as a fallback.
    const socket = io(socketOrigin, { transports: ['websocket', 'polling'], auth: socketAuth });
    socketRef.current = socket;
    // One automatic retry per rejection. A retry runs the auth function
    // again, which usually fixes a token that expired at the wrong moment.
    let authRetried = false;

    socket.on('connect', () => {
      setHostConnected(true);
      socket.emit('join-event', { pin: event.pin });
    });

    socket.on('disconnect', () => {
      setHostConnected(false);
    });

    socket.on('host-auth', ({ pin, operator }: { pin?: string; operator?: boolean }) => {
      if (pin !== event.pin) return;
      if (operator) {
        authRetried = false;
        setHostAuthorized(true);
        return;
      }
      if (!authRetried) {
        authRetried = true;
        socket.disconnect();
        socket.connect();
        return;
      }
      setHostAuthorized(false);
    });

    socket.on('error', ({ message }: { message: string }) => {
      setError(message);
      setTimeout(() => setError(''), 3000);
    });

    // The audience screen forwards audio failures so the host knows to
    // advance manually rather than waiting on a clip that won't load.
    socket.on('audio-error', ({ url }: { url?: string }) => {
      setAudioErrorMsg(`Audio failed to load on the audience screen${url ? ` (${url})` : ''}. Advance manually.`);
    });

    // Sync reminder state from the server's replay so a host who
    // reloads the panel still knows a banner is up on the audience
    // screen — otherwise reminderActive defaults to false and the
    // host has no idea their pre-reload reminder is still showing.
    // The server pushes a new custom-slide list after an operator changes
    // this night's slides through the REST API. The host never sends it.
    socket.on('slides-updated', ({ customSlides: list }: { customSlides?: CustomSlide[] }) => {
      if (Array.isArray(list)) setServerSlides(list);
    });

    socket.on('presentation-update', ({ action, payload }: { action: string; payload?: { text?: string; winnerName?: string } }) => {
      if (action === 'PLAY_DRAWING' || action === 'SHOW_DRAWING') {
        if (drawRevealTimerRef.current !== null) window.clearTimeout(drawRevealTimerRef.current);
        drawRevealTimerRef.current = null;
        setDrawError('');
        const winner = action === 'PLAY_DRAWING' ? (payload?.winnerName ?? '') : '';
        setDrawWinner(winner);
        setDrawStatus(winner ? 'spinning' : 'idle');
        if (winner) {
          drawRevealTimerRef.current = window.setTimeout(() => setDrawStatus('revealed'), DRAWING_SPIN_MS);
        }
      } else if (action === 'SHOW_REMINDER') {
        const text = payload?.text?.trim() ?? '';
        setReminderActive(Boolean(text));
        if (text) setReminderInput(text);
      } else if (action === 'CLEAR_REMINDER') {
        setReminderActive(false);
      }
    });

    return () => {
      socket.disconnect();
      if (drawRevealTimerRef.current !== null) window.clearTimeout(drawRevealTimerRef.current);
    };
  }, [event]);

  const emit = useCallback((action: string, payload: Record<string, unknown> = {}, adminPin?: string) => {
    if (!socketRef.current || !event) return;
    socketRef.current.emit('presentation-control', {
      pin: event.pin,
      action,
      payload,
      adminPin,
    });
  }, [event]);

  // Ask the server to run the drawing. The server picks the winner and
  // broadcasts PLAY_DRAWING. The TV runs the spin, and the
  // presentation-update listener above tracks it for the host view.
  const drawWinnerOnTv = useCallback(async () => {
    if (!event) return;
    setDrawError('');
    setDrawStatus('picking');
    const blocked = await drawingBlockedReason(event.id);
    if (blocked) {
      setDrawError(blocked);
      setDrawStatus('idle');
      return;
    }
    emit('PLAY_DRAWING', {});
    // The server drops a draw with no reply (for example, one sent within a
    // few seconds of the last). Un-stick the button if nothing comes back.
    if (drawRevealTimerRef.current !== null) window.clearTimeout(drawRevealTimerRef.current);
    drawRevealTimerRef.current = window.setTimeout(() => {
      if (drawStatusRef.current !== 'picking') return;
      setDrawError('The TV did not start the drawing. Wait a few seconds and try again.');
      setDrawStatus('idle');
    }, DRAWING_SPIN_MS);
  }, [event, emit]);

  const rounds = event?.quiz?.rounds ?? [];
  const currentRound = rounds[state.currentRound];
  // Phone answer locks for this night (hidden on paper nights).
  const roundLocks = useRoundLocks(id ?? '');
  const currentQuestion = currentRound?.questions?.[state.currentQuestion];

  // Broadcast a ROUND_SUMMARY slide. `visibleCount` controls how many
  // questions are shown; `answerCount` how many answers. Once answerCount
  // > 0 we ship the FULL answer set (the host has already entered the PIN)
  // and let `answerVisibleCount` gate the per-answer reveal — that keeps
  // the audience auto-sizer stable instead of shrinking as each answer
  // pops in. `pin` is only needed on the first reveal of a round; the
  // server skips the gate on subsequent same-round re-emits.
  const emitRoundSummary = useCallback(
    (
      round: Round,
      roundIdx: number,
      opts: { visibleCount: number; answerCount: number; scale: number },
      pin?: string,
    ) => {
      const answersShown = opts.answerCount > 0;
      emit(
        'SHOW_ROUND_SUMMARY',
        {
          roundIdx,
          roundNumber: round.orderIndex + 1,
          roundTitle: round.title,
          roundType: round.roundType,
          totalRounds: rounds.length,
          questions: round.questions.map((q) =>
            answersShown
              ? {
                  orderIndex: q.orderIndex,
                  text: q.text,
                  answer: q.answer,
                  answerPartB: q.answerPartB ?? null,
                  ...choicesForReveal(q),
                }
              : { orderIndex: q.orderIndex, text: q.text, ...choicesForQuestion(q) },
          ),
          answersShown,
          visibleCount: opts.visibleCount,
          answerVisibleCount: opts.answerCount,
          scale: opts.scale,
        },
        pin,
      );
    },
    [emit, rounds],
  );

  // Per-item hide helpers — declared after `rounds` is in scope since
  // itemKey needs roundType to distinguish per-question from per-round
  // slide identities.
  const itemKey = useCallback(
    (slideType: SlideType, roundIdx: number, questionIdx: number, customId?: string): string => {
      // A custom slide is its own item, keyed by placement id.
      if (slideType === 'CUSTOM') return `CUSTOM-${customId ?? ''}`;
      const perQuestion =
        slideType === 'QUESTION' ||
        slideType === 'SONG_BONUS' ||
        (slideType === 'VISUAL_ROUND' && rounds[roundIdx]?.roundType !== 'VISUAL');
      return perQuestion
        ? `${slideType}-${roundIdx}-${questionIdx}`
        : `${slideType}-${roundIdx}`;
    },
    [rounds],
  );

  const isItemHidden = useCallback(
    (slideType: SlideType, roundIdx: number, questionIdx: number, customId?: string): boolean => {
      // Hiding a round hides the round's own slides. Custom slides placed
      // next to it stay, because the host placed them on purpose.
      if (slideType === 'CUSTOM') return hiddenItems.has(itemKey(slideType, roundIdx, questionIdx, customId));
      if (hiddenItems.has(`round-${roundIdx}`)) return true;
      return hiddenItems.has(itemKey(slideType, roundIdx, questionIdx));
    },
    [hiddenItems, itemKey],
  );
  const isEntryHidden = useCallback(
    (e: SeqEntry) => isItemHidden(e.slideType, e.roundIdx, e.questionIdx, e.customId),
    [isItemHidden],
  );

  // The slide sequence for navigation. See utils/slideSequence for the
  // order, including where custom slides go.
  // Rebuild the view when the server list changes or the host moves. The
  // merge keeps the current slide in place, so the position never jumps.
  useEffect(() => {
    setSlidesView((prev) => mergeCustomSlides(
      prev,
      serverSlides,
      state.slideType === 'CUSTOM' ? state.customSlideId : null,
    ));
  }, [serverSlides, state.slideType, state.customSlideId]);
  const customSlides = slidesView;
  const buildSlideSequence = useCallback(() => buildSequence({
    rounds,
    // Resolved rules: venue default / event override fall back to quiz legacy.
    rulesText: event?.welcome?.rulesText ?? event?.quiz?.rulesText ?? null,
    showDrawingSlide: event?.quiz?.showDrawingSlide,
    drawingAfterRound: event?.quiz?.drawingAfterRound,
    showOutroSlide: event?.quiz?.showOutroSlide,
    customSlides,
  }), [
    rounds,
    event?.welcome?.rulesText,
    event?.quiz?.rulesText,
    event?.quiz?.showDrawingSlide,
    event?.quiz?.drawingAfterRound,
    event?.quiz?.showOutroSlide,
    customSlides,
  ]);

  const findCurrentIndex = useCallback(
    () => findSequenceIndex(buildSlideSequence(), state, rounds),
    [buildSlideSequence, state, rounds],
  );

  const navigateTo = useCallback((
    slideType: SlideType,
    roundIdx: number,
    questionIdx: number,
    customId?: string,
  ) => {
    const newState: PresentationState = {
      currentRound: roundIdx,
      currentQuestion: questionIdx,
      answerRevealed: false,
      showingStandings: slideType === 'STANDINGS',
      slideType,
      // Entering a ROUND_SUMMARY resets the reveal counters so the first
      // Next click reveals question 1. Other slide kinds clear them back
      // to 0 just to keep state consistent.
      summaryVisibleCount: 0,
      summaryAnswerCount: 0,
      customSlideId: slideType === 'CUSTOM' ? customId ?? null : null,
    };
    setState(newState);
    // Audio paused-state is per-slide; clear it on any navigation so
    // the next visit to a SONG_BONUS slide starts the toggle in the
    // "playing" position (audio re-fires onLoad).
    setSongPaused(false);

    // A custom slide sends only its placement id. The projector renders it
    // from its own /by-pin copy, so no host-side data (and no answer) can
    // travel with this event.
    if (slideType === 'CUSTOM') {
      emit('SHOW_CUSTOM', { slideId: customId });
      return;
    }

    const round = rounds[roundIdx];
    const question = round?.questions?.[questionIdx];

    const payload: Record<string, unknown> = {
      roundIdx,
      questionIdx,
      roundTitle: round?.title,
      roundType: round?.roundType,
      roundNumber: round ? round.orderIndex + 1 : 0,
      questionNumber: question ? question.orderIndex + 1 : 0,
      questionText: question?.text,
      totalQuestions: round?.questions?.length,
      totalRounds: rounds.length,
    };

    // A question slide carries the choice texts of a multiple choice
    // question and a points tag. Never the correct choice: that goes out
    // only with the PIN-checked reveal.
    if (slideType === 'QUESTION' && question) {
      Object.assign(payload, choicesForQuestion(question));
      const tag = pointsTag(question, round);
      if (tag != null) payload.points = tag;
    }

    // For SONG slides, attach only what the projector needs to play the
    // clip: the audio path and the clip length. Never the title or the
    // artist: this message goes to every screen in the room, and the
    // answer must stay hidden until the reveal.
    if (slideType === 'SONG_BONUS' && question) {
      payload.songAudioPath = question.audioPath ?? '';
      payload.songDuration = question.songDuration ?? null;
    }

    // STANDINGS is rendered after every round. Tell the audience which
    // round's standings these are so it can both label the slide and
    // ask the server for an "as of round N" cutoff — when the host
    // navigates back to round 5's standings during round 7, the
    // displayed leaderboard should reflect end-of-R5 scores only.
    if (slideType === 'STANDINGS' && round) {
      payload.throughRound = round.orderIndex + 1;
    }

    // For ROUND_SUMMARY, ship the round's full question list plus the
    // visible-count counter. Audience shows only the first N. Answers
    // are omitted by default (host clicks Show Answers to reveal them).
    if (slideType === 'ROUND_SUMMARY' && round) {
      payload.questions = round.questions.map((q) => ({
        orderIndex: q.orderIndex,
        text: q.text,
        ...choicesForQuestion(q),
      }));
      payload.answersShown = false;
      payload.visibleCount = 0; // resets when navigating into the summary
      payload.scale = getSummaryScale(roundIdx);
    }

    const actionMap: Record<SlideType, string> = {
      WELCOME: 'SHOW_WELCOME',
      RULES: 'SHOW_RULES',
      ROUNDS_OVERVIEW: 'SHOW_ROUNDS_OVERVIEW',
      ROUND_INTRO: 'SHOW_ROUND_INTRO',
      QUESTION: 'SHOW_QUESTION',
      ANSWER: 'SHOW_ANSWER',
      ROUND_SUMMARY: 'SHOW_ROUND_SUMMARY',
      STANDINGS: 'SHOW_STANDINGS',
      VISUAL_ROUND: 'SHOW_VISUAL_ROUND',
      // Navigating to a song slide just SHOWS it. The host must click
      // Play Song to start playback. (PLAY_SONG is reserved for the
      // explicit play trigger.)
      SONG_BONUS: 'SHOW_SONG',
      DRAWING: 'SHOW_DRAWING',
      OUTRO: 'SHOW_OUTRO',
      CUSTOM: 'SHOW_CUSTOM',
    };

    emit(actionMap[slideType], payload);
  }, [rounds, emit, getSummaryScale]);

  const goNext = useCallback(() => {
    // On a ROUND_SUMMARY slide, Next first reveals questions one at a
    // time until all are visible. Only then does Next advance to the
    // following slide. Show Answers (the host-side button) takes a
    // separate path and skips straight to fully-revealed-with-answers.
    if (state.slideType === 'ROUND_SUMMARY') {
      const round = rounds[state.currentRound];
      const total = round?.questions.length ?? 0;
      // Phase 1: reveal questions one at a time.
      if (state.summaryVisibleCount < total) {
        const nextCount = state.summaryVisibleCount + 1;
        setState((s) => ({ ...s, summaryVisibleCount: nextCount }));
        if (round) {
          emitRoundSummary(round, state.currentRound, {
            visibleCount: nextCount,
            answerCount: state.summaryAnswerCount,
            scale: getSummaryScale(state.currentRound),
          });
        }
        return;
      }
      // Phase 2: once all questions are up AND the host has started the
      // answer reveal (via Show Answers, which clears the PIN gate), Next
      // reveals answers one at a time until every answer is shown.
      if (state.answerRevealed && state.summaryAnswerCount < total) {
        const nextAnswers = state.summaryAnswerCount + 1;
        setState((s) => ({ ...s, summaryAnswerCount: nextAnswers }));
        if (round) {
          emitRoundSummary(round, state.currentRound, {
            visibleCount: total,
            answerCount: nextAnswers,
            scale: getSummaryScale(state.currentRound),
          });
        }
        return;
      }
      // Phase 3 falls through: all questions (and any started answers) are
      // exhausted, so Next advances to the following slide.
    }
    const seq = buildSlideSequence();
    // Walk forward to the next non-hidden item, if there is one.
    const idx = stepSequence(seq, findCurrentIndex(), 1, isEntryHidden);
    if (idx !== -1) {
      const next = seq[idx]!;
      navigateTo(next.slideType, next.roundIdx, next.questionIdx, next.customId);
    }
  }, [state, rounds, emitRoundSummary, buildSlideSequence, findCurrentIndex, navigateTo, isEntryHidden, getSummaryScale]);

  const goPrev = useCallback(() => {
    // Mirror of Next: on summary, Prev first un-reveals answers one at a
    // time, then questions, before falling back to walking the sequence
    // backward.
    if (state.slideType === 'ROUND_SUMMARY') {
      const round = rounds[state.currentRound];
      // Step back through answers first (most-recently-revealed first).
      if (state.summaryAnswerCount > 0) {
        const prevAnswers = state.summaryAnswerCount - 1;
        setState((s) => ({
          ...s,
          summaryAnswerCount: prevAnswers,
          answerRevealed: prevAnswers > 0,
        }));
        if (round) {
          emitRoundSummary(round, state.currentRound, {
            visibleCount: state.summaryVisibleCount,
            answerCount: prevAnswers,
            scale: getSummaryScale(state.currentRound),
          });
        }
        return;
      }
      // Then step back through questions.
      if (state.summaryVisibleCount > 0) {
        const prevCount = state.summaryVisibleCount - 1;
        setState((s) => ({ ...s, summaryVisibleCount: prevCount, answerRevealed: false }));
        if (round) {
          emitRoundSummary(round, state.currentRound, {
            visibleCount: prevCount,
            answerCount: 0,
            scale: getSummaryScale(state.currentRound),
          });
        }
        return;
      }
    }
    const seq = buildSlideSequence();
    const idx = stepSequence(seq, findCurrentIndex(), -1, isEntryHidden);
    if (idx !== -1) {
      const prev = seq[idx]!;
      navigateTo(prev.slideType, prev.roundIdx, prev.questionIdx, prev.customId);
    }
  }, [state, rounds, emitRoundSummary, buildSlideSequence, findCurrentIndex, navigateTo, isEntryHidden, getSummaryScale]);

  // Bump the summary-slide font scale up or down by `delta` (clamped to
  // a sensible range). Re-emits the current summary slide so the audience
  // sees the new size immediately. Persists per round.
  const adjustSummaryScale = useCallback((delta: number) => {
    if (state.slideType !== 'ROUND_SUMMARY') return;
    const round = rounds[state.currentRound];
    if (!round) return;
    const current = getSummaryScale(state.currentRound);
    const next = Math.max(0.5, Math.min(2.5, Math.round((current + delta) * 100) / 100));
    if (next === current) return;
    setSummaryScaleByRound((m) => ({ ...m, [state.currentRound]: next }));
    emitRoundSummary(round, state.currentRound, {
      visibleCount: state.summaryVisibleCount,
      answerCount: state.summaryAnswerCount,
      scale: next,
    });
  }, [
    state.slideType,
    state.currentRound,
    state.summaryVisibleCount,
    state.summaryAnswerCount,
    rounds,
    emitRoundSummary,
    getSummaryScale,
  ]);

  // Start revealing answers on the current ROUND_SUMMARY slide. Maxes the
  // question reveal (so the whole list is up) and shows the FIRST answer;
  // the host then steps through the rest one at a time with Next. PIN-
  // gated like the per-question Show Answer — the server clears the gate
  // for the subsequent per-answer re-emits of the same round.
  const revealSummaryAnswers = useCallback(() => {
    if (state.slideType !== 'ROUND_SUMMARY') return;
    const round = rounds[state.currentRound];
    if (!round) return;
    if (!adminPinInput) {
      setShowPinPrompt(true);
      return;
    }
    const total = round.questions.length;
    const pinForThisCall = adminPinInput;
    setState((s) => ({
      ...s,
      answerRevealed: true,
      summaryVisibleCount: total,
      summaryAnswerCount: 1,
    }));
    // Clear the PIN immediately so the next round's reveal re-prompts the
    // host. Single-use rather than session-wide.
    setAdminPinInput('');
    emitRoundSummary(
      round,
      state.currentRound,
      { visibleCount: total, answerCount: 1, scale: getSummaryScale(state.currentRound) },
      pinForThisCall,
    );
  }, [state.slideType, state.currentRound, rounds, emitRoundSummary, adminPinInput, getSummaryScale]);

  const showAnswer = useCallback(() => {
    if (!currentQuestion || !event) return;
    if (state.slideType !== 'QUESTION' && state.slideType !== 'ANSWER') return;
    if (!adminPinInput) {
      setShowPinPrompt(true);
      return;
    }

    const round = rounds[state.currentRound];
    const pinForThisCall = adminPinInput;
    setState((s) => ({ ...s, answerRevealed: true, slideType: 'ANSWER' }));
    // Single-use PIN: clear so the next reveal re-prompts.
    setAdminPinInput('');
    emit('SHOW_ANSWER', {
      roundIdx: state.currentRound,
      questionIdx: state.currentQuestion,
      roundNumber: round ? round.orderIndex + 1 : 0,
      questionNumber: currentQuestion.orderIndex + 1,
      questionText: currentQuestion.text,
      answer: currentQuestion.answer,
      answerPartB: currentQuestion.answerPartB,
      ...choicesForReveal(currentQuestion),
    }, pinForThisCall);
  }, [currentQuestion, event, adminPinInput, rounds, state, emit]);

  const showStandings = useCallback(() => {
    setState((s) => ({ ...s, showingStandings: true, slideType: 'STANDINGS' }));
    emit('SHOW_STANDINGS', { eventId: event?.id });
  }, [event, emit]);

  const playSong = useCallback(() => {
    // Songs now live on individual SONG-type questions. If the host clicks
    // "Play song" we expect them to be on a SONG question already.
    if (!currentQuestion || currentQuestion.type !== 'SONG') return;
    setState((s) => ({ ...s, slideType: 'SONG_BONUS' }));
    setSongPaused(false);
    const payload: Record<string, unknown> = {
      roundIdx: state.currentRound,
      questionIdx: state.currentQuestion,
      // No title or artist: see the SONG slide payload above.
      songAudioPath: currentQuestion.audioPath ?? '',
      songDuration: currentQuestion.songDuration ?? null,
    };
    const round = rounds[state.currentRound];
    if (round) {
      payload.roundNumber = round.orderIndex + 1;
      payload.roundTitle = round.title;
      payload.totalQuestions = round.questions.length;
    }
    payload.questionNumber = currentQuestion.orderIndex + 1;
    emit('PLAY_SONG', payload);
  }, [currentQuestion, rounds, state.currentRound, state.currentQuestion, emit]);

  // ── Audio playback controls — toggle pause/resume + restart. The
  // audio element lives on the audience screen; we just tell it what
  // to do via socket. State is local to the host so we know whether
  // to show "Pause" or "Resume".
  const [songPaused, setSongPaused] = useState(false);

  const toggleSong = useCallback(() => {
    if (songPaused) {
      emit('RESUME_SONG', {});
      setSongPaused(false);
    } else {
      emit('PAUSE_SONG', {});
      setSongPaused(true);
    }
  }, [songPaused, emit]);

  const restartSong = useCallback(() => {
    emit('RESTART_SONG', {});
    setSongPaused(false);
  }, [emit]);

  // Keyboard navigation. A is HOLD-TO-PEEK (host-only local reveal), not
  // projector reveal — projecting the answer is click + PIN only, so a
  // stray A press can never spoil the room. ←/→ nav, S standings.
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          goNext();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          goPrev();
          break;
        case 'a':
        case 'A':
          e.preventDefault();
          if (!e.repeat) setPeeking(true);
          break;
        case 's':
        case 'S':
          e.preventDefault();
          showStandings();
          break;
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.key === 'a' || e.key === 'A') setPeeking(false);
    };
    // A window blur (alt-tab, focus lost) must drop the peek or the answer
    // could stay revealed on the host screen unattended.
    const blur = () => setPeeking(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, [goNext, goPrev, showStandings]);

  // Stopwatch — restart from 0 every time the current slide identity
  // changes (slide kind, round, or question). Updates the elapsed
  // counter once per second while parked on a slide. Host-only; no
  // socket emit, so the audience screen stays clean.
  useEffect(() => {
    setSlideEnteredAt(Date.now());
    setElapsedSeconds(0);
  }, [state.slideType, state.currentRound, state.currentQuestion]);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - slideEnteredAt) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [slideEnteredAt]);

  const formatStopwatch = (secs: number): string => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const toggleRound = (idx: number) => {
    setExpandedRounds((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const jumpToRound = (roundIdx: number) => {
    navigateTo('ROUND_INTRO', roundIdx, 0);
  };

  // jumpToQuestion is no longer needed — the outline now calls navigateTo
  // directly with the slide kind matching the question's type. Kept removed
  // rather than prefixed-underscored so the dead code doesn't confuse future
  // readers.

  const confirmAdminPin = () => {
    if (!adminPinInput || adminPinInput.length < 4) return;
    setShowPinPrompt(false);
    showAnswer();
  };

  if (loading) return <Theme hasBackground={false}><div className="host-panel-page"><div className="loading-msg">Loading event...</div></div></Theme>;
  if (error && !event) return <Theme hasBackground={false}><div className="host-panel-page"><div className="error-msg">{error}</div></div></Theme>;
  if (!event) return <Theme hasBackground={false}><div className="host-panel-page"><div className="error-msg">Event not found</div></div></Theme>;

  const missingKeys = missingChoiceKeys(rounds);

  const currentCustom = state.slideType === 'CUSTOM'
    ? slidesView.find((c) => c.id === state.customSlideId)
    : undefined;

  const slideLabel = () => {
    switch (state.slideType) {
      case 'WELCOME': return 'Welcome / Idle';
      case 'RULES': return 'Rules';
      case 'ROUNDS_OVERVIEW': return "Tonight's Rounds";
      case 'ROUND_INTRO': return `Round ${currentRound ? currentRound.orderIndex + 1 : '?'} Intro`;
      case 'QUESTION': return `Q${currentQuestion ? currentQuestion.orderIndex + 1 : '?'}`;
      case 'ANSWER': return `Answer Revealed`;
      case 'ROUND_SUMMARY': return `Round ${currentRound ? currentRound.orderIndex + 1 : '?'} Summary`;
      case 'STANDINGS': return 'Standings';
      case 'VISUAL_ROUND': return 'Visual Round';
      case 'SONG_BONUS': return `Song · Q${currentQuestion ? currentQuestion.orderIndex + 1 : '?'}`;
      case 'DRAWING': return 'Random Drawing';
      case 'OUTRO': return "Outro — Schedule & Socials";
      case 'CUSTOM': return currentCustom ? customSlideLabel(currentCustom) : 'Custom slide';
      default: return '';
    }
  };

  // Short human label for a slide-sequence entry — feeds the "UP NEXT" card.
  const describeSeqEntry = (entry: SeqEntry | undefined): string => {
    if (!entry) return 'End of show';
    const r = rounds[entry.roundIdx];
    const q = r?.questions?.[entry.questionIdx];
    switch (entry.slideType) {
      case 'RULES': return 'Rules';
      case 'ROUNDS_OVERVIEW': return "Tonight's Rounds";
      case 'ROUND_INTRO': return `Round ${r ? r.orderIndex + 1 : '?'} intro`;
      case 'QUESTION': return `Q${q ? q.orderIndex + 1 : '?'}`;
      case 'SONG_BONUS': {
        const dur = q?.songDuration ? `, ${Math.floor(q.songDuration / 60)}:${String(q.songDuration % 60).padStart(2, '0')}` : '';
        const ready = q?.audioPath ? ' — ready' : ' — no clip';
        return `Q${q ? q.orderIndex + 1 : '?'} · ♪ Song clip${dur}${ready}`;
      }
      case 'VISUAL_ROUND': return q ? `Q${q.orderIndex + 1} · 🖼 Visual` : 'Visual round';
      case 'ROUND_SUMMARY': return `Round ${r ? r.orderIndex + 1 : '?'} summary`;
      case 'STANDINGS': return 'Standings';
      case 'DRAWING': return 'Random drawing';
      case 'OUTRO': return 'Outro';
      case 'CUSTOM': {
        const c = slidesView.find((x) => x.id === entry.customId);
        return c ? customSlideLabel(c) : 'Custom slide';
      }
      case 'WELCOME': return 'Welcome';
      default: return slideLabel();
    }
  };

  // Next non-hidden slide, for the UP NEXT card. Mirrors goNext's skip logic.
  const upNextLabel = (() => {
    const seq = buildSlideSequence();
    const idx = stepSequence(seq, findCurrentIndex(), 1, isEntryHidden);
    return describeSeqEntry(idx === -1 ? undefined : seq[idx]);
  })();

  // The answer is legible on the host's own screen only when privacy is off
  // or the host is actively peeking (holding A / the peek button).
  const answerVisibleLocally = !privacyMode || peeking;

  // Answer text for the host's ANSWER card (never projected from here).
  // A multiple choice answer leads with its letter ("B. Paris").
  const answerText = !currentQuestion
    ? ''
    : isMultipleChoice(currentQuestion) && currentQuestion.correctChoice != null
      ? `${choiceLetter(currentQuestion.correctChoice)}. ${currentQuestion.answer}`
      : (songAnswerText(currentQuestion) || '—');
  const answerPartB = currentQuestion?.answerPartB;

  const startPeek = () => setPeeking(true);
  const endPeek = () => setPeeking(false);

  return (
    <Theme hasBackground={false}>
    <div className="host-panel-page">
      {/* Top strip */}
      <div className="host-strip">
        <div className="strip-title">
          <span className="strip-event">{event.name?.trim() || event.quiz.name}</span>
          <span className="strip-venue">{event.venue.name}</span>
        </div>
        <span className="strip-spacer" />
        <span className={`strip-conn ${hostConnected ? 'ok' : 'down'}`}>
          <span className="strip-conn-dot" />
          {hostConnected ? 'Connected' : 'Disconnected'}
        </span>
        <span className="strip-pin">PIN <strong>{event.pin}</strong></span>
        <span className="strip-pin strip-pin-admin" title="Admin PIN — type this to reveal an answer on the projector">
          ADMIN <strong>{event.adminPin}</strong>
        </span>
        <button
          type="button"
          className={`strip-privacy ${privacyMode ? 'on' : 'off'}`}
          onClick={togglePrivacy}
          title={privacyMode
            ? 'Privacy on — answers hidden on your screen until you hold A. Click to turn off.'
            : 'Privacy off — answers shown plainly on your screen. Click to turn on.'}
        >
          👁 PRIVACY {privacyMode ? 'ON' : 'OFF'}
        </button>
        <div className="strip-reminder">
          <button
            type="button"
            className="strip-btn"
            onClick={() => setReminderOpen((v) => !v)}
            aria-expanded={reminderOpen}
          >
            Reminder ▾
          </button>
          {reminderOpen && (
            <>
              <div className="strip-pop-overlay" onClick={() => setReminderOpen(false)} />
              <div className="strip-pop">
                <div className="strip-pop-row">
                  <TextField.Root
                    size="2"
                    type="text"
                    style={{ flex: 1 }}
                    placeholder="Type a message…"
                    value={reminderInput}
                    onChange={(e) => setReminderInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') sendReminder(reminderInput); }}
                  />
                  <Button size="2" onClick={() => sendReminder(reminderInput)} disabled={!reminderInput.trim()}>
                    Send
                  </Button>
                </div>
                <div className="strip-pop-presets">
                  {REMINDER_PRESETS.map((preset) => (
                    <button key={preset} className="btn-preset" onClick={() => sendReminder(preset)}>
                      {preset}
                    </button>
                  ))}
                </div>
                {reminderActive && (
                  <button className="strip-pop-dismiss" type="button" onClick={clearReminder}>
                    Dismiss live banner
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          className="strip-btn"
          onClick={() => { setRoundSheetRound(state.currentRound); setShowRoundSheet(true); }}
          title="Show every question in the current round with answers"
        >
          Round sheet
        </button>
        <a className="strip-btn" href={`/events/${event.id}/score`} target="_blank" rel="noopener noreferrer">
          Scoring
        </a>
        <a className="strip-btn" href={`/present/${event.pin}`} target="_blank" rel="noopener noreferrer">
          Projector view ↗
        </a>
      </div>

      {error && <div className="host-error">{error}</div>}

      {/* Disconnect banner — controls are disabled below while this is
          showing, so the host can't blindly fire emits into nothing. */}
      {!hostConnected && (
        <div className="host-banner host-banner-danger">
          ⚠ Disconnected from server. Reconnecting… Controls disabled until reconnect.
        </div>
      )}

      {/* Auth banner — the server does not accept controls from this
          socket, so slide changes do not reach the projector. */}
      {hostConnected && !hostAuthorized && (
        <div className="host-banner host-banner-danger">
          ⚠ This screen is not signed in as a host for this game night. Slide changes do not reach the projector. Reload the page or sign in again.
        </div>
      )}

      {/* Multiple choice questions with no correct choice. Phones mark
          every answer to them wrong, so warn before the night starts. */}
      {missingKeys.length > 0 && (
        <div className="host-banner host-banner-warning" role="alert">
          <span>
            ⚠ {missingKeys.length} multiple choice {missingKeys.length === 1 ? 'question has' : 'questions have'} no correct choice.
            Phones mark every answer wrong. Fix:{' '}
            {missingKeys.map((m, i) => (
              <span key={m.questionId}>
                {i > 0 ? ', ' : ''}
                <Link
                  to={`/quizzes/${event.quiz.id}/edit?question=${encodeURIComponent(m.questionId)}`}
                  title={m.text}
                  style={{ color: 'inherit', fontWeight: 700 }}
                >
                  {missingChoiceLabel(m)}
                </Link>
              </span>
            ))}
          </span>
        </div>
      )}

      {/* Audience audio error — pushed from the audience screen's
          onError handler so the host knows to advance instead of
          waiting for a dead clip to play. */}
      {audioErrorMsg && (
        <div className="host-banner host-banner-warning">
          {audioErrorMsg}
          <button
            type="button"
            onClick={() => setAudioErrorMsg(null)}
            style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '1.1rem' }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Live-reminder banner. Sits at the top whenever a reminder is
          showing on the audience screen so the host can always dismiss
          it without having to expand the Reminder panel. */}
      {reminderActive && (
        <div className="host-banner host-banner-warning" style={{ background: 'var(--accent-a3)', borderColor: 'var(--accent-a6)' }}>
          <span>Reminder is live on audience screen{reminderInput ? `: "${reminderInput}"` : ''}.</span>
          <button
            type="button"
            onClick={clearReminder}
            style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid currentColor', color: 'inherit', cursor: 'pointer', fontSize: '0.85rem', padding: '0.25rem 0.7rem', borderRadius: 6 }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Admin PIN prompt modal */}
      {showPinPrompt && (
        <div className="pin-modal-overlay" onClick={() => setShowPinPrompt(false)}>
          <div className="pin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Enter Admin PIN</h3>
            <p>Required to reveal answers</p>
            <input
              type="password"
              maxLength={4}
              inputMode="numeric"
              value={adminPinInput}
              onChange={(e) => setAdminPinInput(e.target.value)}
              placeholder="4-digit PIN"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') confirmAdminPin(); }}
              // Tell password managers (1Password, LastPass, Bitwarden, browser
              // autofill) to leave this alone — it's a per-event 4-digit PIN,
              // not a saveable password. The masked-dots UI still works.
              autoComplete="one-time-code"
              name="admin-pin"
              data-1p-ignore=""
              data-lpignore="true"
              data-bwignore=""
              data-form-type="other"
            />
            <div className="pin-modal-actions">
              <Button onClick={confirmAdminPin}>Confirm</Button>
              <Button variant="soft" color="gray" onClick={() => setShowPinPrompt(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      <div className="host-columns">
        {/* Left column — Quiz outline */}
        <div className="host-outline">
          <h2>Quiz Outline</h2>
          <button
            className={`outline-item welcome-item ${state.slideType === 'WELCOME' ? 'active' : ''}`}
            onClick={() => navigateTo('WELCOME', 0, 0)}
          >
            Welcome Screen
          </button>
          {(event.welcome?.rulesText ?? event.quiz.rulesText)?.trim() && (
            <button
              className={`outline-item welcome-item ${state.slideType === 'RULES' ? 'active' : ''}`}
              onClick={() => navigateTo('RULES', 0, 0)}
            >
              Rules
            </button>
          )}
          {rounds.length > 0 && (
            <button
              className={`outline-item welcome-item ${state.slideType === 'ROUNDS_OVERVIEW' ? 'active' : ''}`}
              onClick={() => navigateTo('ROUNDS_OVERVIEW', 0, 0)}
            >
              Tonight&apos;s Rounds
            </button>
          )}
          {rounds.map((round, ri) => {
            const roundHidden = hiddenItems.has(`round-${ri}`);
            // Helper for per-item eye buttons inside this round
            const eye = (slideType: SlideType, qi: number) => {
              const key = itemKey(slideType, ri, qi);
              const hidden = hiddenItems.has(key);
              return (
                <button
                  type="button"
                  className={`outline-eye ${hidden ? 'eye-hidden' : ''}`}
                  onClick={(e) => { e.stopPropagation(); toggleHidden(key); }}
                  title={hidden ? 'Show in presentation' : 'Hide from presentation'}
                  aria-label={hidden ? 'Show' : 'Hide'}
                >
                  {hidden ? '⊘' : '👁'}
                </button>
              );
            };
            return (
              <div key={round.id} className={`outline-round ${roundHidden ? 'item-hidden' : ''}`}>
                <div className="outline-row">
                  <button
                    className={`outline-round-header ${state.currentRound === ri && state.slideType !== 'WELCOME' && state.slideType !== 'STANDINGS' ? 'active' : ''}`}
                    onClick={() => toggleRound(ri)}
                  >
                    <span className="round-indicator">{round.orderIndex + 1}</span>
                    <span className="round-title">{round.title}</span>
                    <span className={`round-type-badge ${round.roundType.toLowerCase()}`}>{round.roundType}</span>
                    <span className={`expand-arrow ${expandedRounds.has(ri) ? 'expanded' : ''}`}>&#9654;</span>
                  </button>
                  <button
                    type="button"
                    className={`outline-eye ${roundHidden ? 'eye-hidden' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleHidden(`round-${ri}`); }}
                    title={roundHidden ? 'Show round in presentation' : 'Hide whole round from presentation'}
                    aria-label={roundHidden ? 'Show round' : 'Hide round'}
                  >
                    {roundHidden ? '⊘' : '👁'}
                  </button>
                </div>
                {expandedRounds.has(ri) && (
                  <div className="outline-questions">
                    <div className={`outline-row ${hiddenItems.has(itemKey('ROUND_INTRO', ri, 0)) ? 'item-hidden' : ''}`}>
                      <button
                        className={`outline-question-item ${state.currentRound === ri && state.slideType === 'ROUND_INTRO' ? 'active' : ''}`}
                        onClick={() => jumpToRound(ri)}
                      >
                        Round Intro
                      </button>
                      {eye('ROUND_INTRO', 0)}
                    </div>
                    {round.roundType === 'VISUAL' ? (
                      <div className={`outline-row ${hiddenItems.has(itemKey('VISUAL_ROUND', ri, 0)) ? 'item-hidden' : ''}`}>
                        <button
                          className={`outline-question-item ${state.currentRound === ri && state.slideType === 'VISUAL_ROUND' ? 'active' : ''}`}
                          onClick={() => navigateTo('VISUAL_ROUND', ri, 0)}
                        >
                          Visual Round
                        </button>
                        {eye('VISUAL_ROUND', 0)}
                      </div>
                    ) : (
                      <>
                        {round.questions.map((q, qi) => {
                          const slideTypeForQ: SlideType =
                            q.type === 'SONG' ? 'SONG_BONUS' :
                            q.type === 'IMAGE' ? 'VISUAL_ROUND' :
                                                  'QUESTION';
                          const isActive =
                            state.currentRound === ri &&
                            state.currentQuestion === qi &&
                            (state.slideType === slideTypeForQ ||
                              (slideTypeForQ === 'QUESTION' && state.slideType === 'ANSWER'));
                          const label =
                            q.type === 'SONG'  ? `♪ ${qi + 1}` :
                            q.type === 'IMAGE' ? `🖼 ${qi + 1}` :
                                                  `Q${q.orderIndex + 1}`;
                          return (
                            <div key={q.id} className={`outline-row ${hiddenItems.has(itemKey(slideTypeForQ, ri, qi)) ? 'item-hidden' : ''}`}>
                              <button
                                className={`outline-question-item ${q.type === 'SONG' ? 'song-item' : ''} ${isActive ? 'active' : ''}`}
                                onClick={() => navigateTo(slideTypeForQ, ri, qi)}
                              >
                                {label}
                              </button>
                              {eye(slideTypeForQ, qi)}
                            </div>
                          );
                        })}
                      </>
                    )}
                    {round.questions.length > 0 && (
                      <div className={`outline-row ${hiddenItems.has(itemKey('ROUND_SUMMARY', ri, 0)) ? 'item-hidden' : ''}`}>
                        <button
                          className={`outline-question-item ${state.currentRound === ri && state.slideType === 'ROUND_SUMMARY' ? 'active' : ''}`}
                          onClick={() => navigateTo('ROUND_SUMMARY', ri, 0)}
                        >
                          {round.roundType === 'AUDIO' ? 'Answers' : 'Round Summary'}
                        </button>
                        {eye('ROUND_SUMMARY', 0)}
                      </div>
                    )}
                    <div className={`outline-row ${hiddenItems.has(itemKey('STANDINGS', ri, 0)) ? 'item-hidden' : ''}`}>
                      <button
                        className={`outline-question-item ${state.currentRound === ri && state.slideType === 'STANDINGS' ? 'active' : ''}`}
                        onClick={() => navigateTo('STANDINGS', ri, 0)}
                      >
                        Standings
                      </button>
                      {eye('STANDINGS', 0)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <button
            className={`outline-item standings-item ${state.slideType === 'STANDINGS' && state.currentRound === rounds.length - 1 ? 'active' : ''}`}
            onClick={() => navigateTo('STANDINGS', rounds.length - 1, 0)}
            title="Jump to final standings"
          >
            Final Standings
          </button>
          {rounds.length > 0 && (
            <button
              className={`outline-item standings-item ${state.slideType === 'DRAWING' ? 'active' : ''}`}
              onClick={() => navigateTo('DRAWING', rounds.length - 1, 0)}
              title="Random drawing — picks from teams not in 1st/2nd and still here"
            >
              Random Drawing
            </button>
          )}
          {rounds.length > 0 && (
            <button
              className={`outline-item standings-item ${state.slideType === 'OUTRO' ? 'active' : ''}`}
              onClick={() => navigateTo('OUTRO', rounds.length - 1, 0)}
              title="End-of-night slide with upcoming schedule and social QR codes"
            >
              Outro
            </button>
          )}
          {slidesView.length > 0 && (
            <>
              <h2 className="outline-subhead">Your Slides</h2>
              {slidesView.map((c) => {
                const key = itemKey('CUSTOM', c.roundIdx, 0, c.id);
                const hidden = hiddenItems.has(key);
                return (
                  <div key={c.id} className={`outline-row ${hidden ? 'item-hidden' : ''}`}>
                    <button
                      className={`outline-question-item custom-item ${state.slideType === 'CUSTOM' && state.customSlideId === c.id ? 'active' : ''}`}
                      onClick={() => navigateTo('CUSTOM', c.roundIdx, 0, c.id)}
                      title={placementLabel(c)}
                    >
                      {customSlideLabel(c)}
                      <span className="custom-item-pos">{placementLabel(c)}</span>
                    </button>
                    <button
                      type="button"
                      className={`outline-eye ${hidden ? 'eye-hidden' : ''}`}
                      onClick={(e) => { e.stopPropagation(); toggleHidden(key); }}
                      title={hidden ? 'Show in presentation' : 'Hide from presentation'}
                      aria-label={hidden ? 'Show' : 'Hide'}
                    >
                      {hidden ? '⊘' : '👁'}
                    </button>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Center — Current state preview + info cards + control dock */}
        <div className="host-preview">
          <div className="preview-label">
            <span className="preview-overline">
              {(state.slideType === 'QUESTION' || state.slideType === 'ANSWER' || state.slideType === 'SONG_BONUS') && currentQuestion && currentRound
                ? `ROUND ${currentRound.orderIndex + 1} · QUESTION ${currentQuestion.orderIndex + 1} OF ${currentRound.questions.length}`
                : slideLabel().toUpperCase()}
            </span>
            <span className="preview-timer">{formatStopwatch(elapsedSeconds)} on slide</span>
            <span className="strip-spacer" />
            <div className="preview-size">
              <IconButton
                size="1"
                variant="soft"
                color="gray"
                type="button"
                aria-label="Shrink slide preview"
                title="Shrink slide preview"
                disabled={previewScale <= PREVIEW_SCALE_MIN}
                onClick={() => adjustPreviewScale(-PREVIEW_SCALE_STEP)}
              >
                −
              </IconButton>
              <span className="preview-size-value">
                {Math.round(previewScale * 100)}%
              </span>
              <IconButton
                size="1"
                variant="soft"
                color="gray"
                type="button"
                aria-label="Enlarge slide preview"
                title="Enlarge slide preview"
                disabled={previewScale >= PREVIEW_SCALE_MAX}
                onClick={() => adjustPreviewScale(PREVIEW_SCALE_STEP)}
              >
                +
              </IconButton>
            </div>
          </div>
          <div
            className="preview-card"
            style={{ ['--preview-scale' as string]: previewScale }}
          >
            {state.slideType === 'WELCOME' && (
              <div className="preview-content welcome">
                <h2>{brandName}</h2>
                <p>PIN: {event.pin}</p>
                {(event.welcome?.startTime ?? event.quiz.welcomeStartTime) && <p>Starts: {event.welcome?.startTime ?? event.quiz.welcomeStartTime}</p>}
                {(event.welcome?.prizes ?? event.quiz.welcomePrizes) && <p>Prizes: {event.welcome?.prizes ?? event.quiz.welcomePrizes}</p>}
              </div>
            )}
            {state.slideType === 'RULES' && (
              <div className="preview-content welcome">
                <h2>Rules</h2>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', lineHeight: 1.5 }}>
                  {((event.welcome?.rulesText ?? event.quiz.rulesText) || '')
                    .split(/\r?\n/)
                    .map((l) => l.trim())
                    .filter(Boolean)
                    .map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              </div>
            )}
            {state.slideType === 'ROUNDS_OVERVIEW' && (
              <div className="preview-content welcome">
                <h2>Tonight&apos;s Rounds</h2>
                <ol style={{ margin: 0, paddingLeft: '1.5rem', lineHeight: 1.6 }}>
                  {rounds.map((r) => (
                    <li key={r.id}>
                      {r.title || 'Untitled'}
                      {r.roundType !== 'STANDARD' && ` (${r.roundType.toLowerCase()})`}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {state.slideType === 'ROUND_INTRO' && currentRound && (
              <div className="preview-content round-intro">
                <span className="preview-round-num">Round {currentRound.orderIndex + 1}</span>
                <h2>{currentRound.title}</h2>
                <span className={`preview-type ${currentRound.roundType.toLowerCase()}`}>{currentRound.roundType}</span>
              </div>
            )}
            {(state.slideType === 'QUESTION' || state.slideType === 'ANSWER') && currentQuestion && (
              <div className="preview-content question">
                <div className="preview-q-header">
                  <span>Round {currentRound?.orderIndex !== undefined ? currentRound.orderIndex + 1 : '?'}</span>
                  <span>Question {currentQuestion.orderIndex + 1}</span>
                </div>
                <FitHeading text={currentQuestion.text} />
                {isMultipleChoice(currentQuestion) && (
                  <ol className="preview-choices">
                    {currentQuestion.choices!.map((c, i) => (
                      <li
                        key={i}
                        className={state.answerRevealed && i === currentQuestion.correctChoice ? 'correct' : ''}
                      >
                        <span className="preview-choice-letter">{choiceLetter(i)}</span> {c}
                      </li>
                    ))}
                  </ol>
                )}
                {state.answerRevealed && (
                  <div className="preview-answer">
                    <span className="answer-label">Answer:</span>
                    <span className="answer-text">{currentQuestion.answer}</span>
                    {currentQuestion.answerPartB && (
                      <span className="answer-text part-b">{currentQuestion.answerPartB}</span>
                    )}
                  </div>
                )}
                {currentQuestion.notes && (
                  <div className="preview-notes">
                    <span className="notes-label">Host Notes:</span> {currentQuestion.notes}
                  </div>
                )}
              </div>
            )}
            {state.slideType === 'ROUND_SUMMARY' && currentRound && (
              <div className="preview-content summary">
                <h2>Round {currentRound.orderIndex + 1} Summary</h2>
                <p>
                  {currentRound.title}
                  {' '}
                  <small style={{ opacity: 0.7 }}>
                    ({state.summaryVisibleCount}/{currentRound.questions.length} shown
                    {state.summaryAnswerCount > 0 &&
                      `, ${state.summaryAnswerCount}/${currentRound.questions.length} answers`}
                    )
                  </small>
                </p>
                <ol className="summary-list">
                  {currentRound.questions.slice(0, state.summaryVisibleCount).map((q, i) => (
                    <li key={q.id}>
                      <span>{q.text || '(no text)'}</span>
                      {i < state.summaryAnswerCount && (
                        <span className="summary-answer">
                          {' '}— <strong>{q.answer || '—'}</strong>
                          {q.answerPartB && (
                            <strong> / {q.answerPartB}</strong>
                          )}
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {state.slideType === 'STANDINGS' && (
              <div className="preview-content standings">
                <h2>Standings</h2>
                <p>Showing on presentation screen</p>
              </div>
            )}
            {state.slideType === 'DRAWING' && (
              <div className="preview-content welcome">
                <h2>Random Drawing</h2>
                {drawStatus === 'spinning' && <p>Spinning on the TV…</p>}
                {drawStatus === 'revealed' && <p>Winner: <strong>{drawWinner}</strong></p>}
                {(drawStatus === 'idle' || drawStatus === 'picking') && (
                  <p>Click Draw winner below, or have someone click Draw on the TV. The winner comes from teams ranked 3rd or lower that are still here.</p>
                )}
                {drawError && <p className="drawing-host-error">{drawError}</p>}
              </div>
            )}
            {state.slideType === 'OUTRO' && (
              <div className="preview-content welcome">
                <h2>Outro</h2>
                <p>Audience sees your next few events and QR codes for your website and Instagram. Schedule pulls live from /events/upcoming.</p>
              </div>
            )}
            {state.slideType === 'CUSTOM' && (
              <div className="preview-content welcome">
                <h2>{currentCustom ? customSlideLabel(currentCustom) : 'Custom slide'}</h2>
                {currentCustom?.body && <p style={{ whiteSpace: 'pre-line' }}>{currentCustom.body}</p>}
                {currentCustom?.kind === 'IMAGE' && currentCustom.imageUrl && (
                  <img src={currentCustom.imageUrl} alt="" style={{ maxWidth: '100%', maxHeight: '12rem', objectFit: 'contain' }} />
                )}
                {currentCustom?.kind === 'PROMO' && <p>QR code: {currentCustom.url || 'no link set'}</p>}
                {currentCustom?.kind === 'SCHEDULE' && (
                  <p>{currentCustom.schedule?.length ?? 0} schedule entries for this venue.</p>
                )}
              </div>
            )}
            {state.slideType === 'VISUAL_ROUND' && currentRound && (
              <div className="preview-content visual">
                <h2>Visual Round</h2>
                <p>{currentRound.title}</p>
              </div>
            )}
            {state.slideType === 'SONG_BONUS' && currentQuestion && (
              <div className="preview-content song">
                <h2>Song Question</h2>
                {songAnswerText(currentQuestion) && <p>{songAnswerText(currentQuestion)}</p>}
              </div>
            )}
          </div>

          {/* Info cards under the preview: the host's private ANSWER view
              (blurred until peek) and the UP NEXT summary. */}
          <div className="host-info-cards">
            <div className={`answer-card ${answerVisibleLocally ? 'revealed' : 'hidden'}`}>
              <div className="answer-card-body">
                <span className="answer-card-overline">
                  {privacyMode ? (answerVisibleLocally ? 'ANSWER · PEEKING' : 'ANSWER · HIDDEN') : 'ANSWER'}
                </span>
                <div className={`answer-card-text ${answerVisibleLocally ? '' : 'obscured'}`}>
                  {(state.slideType === 'QUESTION' || state.slideType === 'ANSWER' || state.slideType === 'SONG_BONUS') && currentQuestion
                    ? <>{answerText}{answerPartB ? ` / ${answerPartB}` : ''}</>
                    : <span className="answer-card-na">No answer on this slide</span>}
                </div>
              </div>
              {privacyMode && (
                <button
                  type="button"
                  className="answer-peek-btn"
                  onMouseDown={startPeek}
                  onMouseUp={endPeek}
                  onMouseLeave={endPeek}
                  onTouchStart={(e) => { e.preventDefault(); startPeek(); }}
                  onTouchEnd={endPeek}
                  title="Hold to reveal the answer on your screen only"
                >
                  Hold <kbd>A</kbd> to peek
                </button>
              )}
            </div>
            <div className="upnext-card">
              <span className="upnext-overline">UP NEXT</span>
              <div className="upnext-text">{upNextLabel}</div>
            </div>
          </div>

          {/* Contextual controls — appear in the dock when the slide has
              extra actions (song playback, summary reveal, drawing reset). */}
          {(currentQuestion?.type === 'SONG' || state.slideType === 'ROUND_SUMMARY' || state.slideType === 'DRAWING') && (
            <div className="dock-context">
              {currentQuestion?.type === 'SONG' && (
                <>
                  <Button size="2" color="green" onClick={playSong}>▶ Play song</Button>
                  {state.slideType === 'SONG_BONUS' && (
                    <>
                      <Button size="2" variant="soft" color="gray" type="button" onClick={toggleSong}
                        title={songPaused ? 'Resume on audience screen' : 'Pause on audience screen'}>
                        {songPaused ? '▶ Resume' : '⏸ Pause'}
                      </Button>
                      <Button size="2" variant="soft" color="gray" type="button" onClick={restartSong} title="Restart from the beginning">
                        ⟲ Restart
                      </Button>
                    </>
                  )}
                </>
              )}
              {state.slideType === 'ROUND_SUMMARY' && (
                <>
                  <Button size="2" color="red" onClick={revealSummaryAnswers} disabled={state.answerRevealed}
                    title="Reveal the first answer, then press → for the rest">
                    {state.answerRevealed
                      ? `Revealing (${state.summaryAnswerCount}/${currentRound?.questions.length ?? 0}) — press →`
                      : 'Show answers on projector'}
                  </Button>
                  <span className="dock-context-scale">
                    <span className="summary-scale-label">Text</span>
                    <IconButton size="1" variant="soft" color="gray" onClick={() => adjustSummaryScale(-0.1)} aria-label="Decrease summary text size">−</IconButton>
                    <span className="summary-scale-value">{Math.round(getSummaryScale(state.currentRound) * 100)}%</span>
                    <IconButton size="1" variant="soft" color="gray" onClick={() => adjustSummaryScale(0.1)} aria-label="Increase summary text size">+</IconButton>
                  </span>
                </>
              )}
              {state.slideType === 'DRAWING' && (
                <>
                  <Button size="2" color="green" type="button"
                    onClick={drawWinnerOnTv}
                    disabled={!hostConnected || drawStatus === 'picking' || drawStatus === 'spinning'}
                    title="Pick a winner and run the spin on the TV">
                    🎲 {drawStatus === 'revealed' ? 'Draw again' : 'Draw winner'}
                  </Button>
                  <Button size="2" variant="soft" color="gray" type="button"
                    onClick={() => navigateTo('DRAWING', rounds.length - 1, 0)}
                    title="Reset the drawing back to the Draw button">
                    ⟲ Reset drawing
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Control dock — the host's hands live here. Next dominates. */}
          <div className="host-dock">
            <button className="dock-btn dock-prev" onClick={goPrev} disabled={!hostConnected} title="Previous (←)">
              ← Prev
            </button>
            <button className="dock-btn dock-next" onClick={goNext} disabled={!hostConnected} title="Next (→)">
              Next →
            </button>
            <button
              className="dock-btn dock-reveal"
              onClick={showAnswer}
              disabled={!hostConnected || (state.slideType !== 'QUESTION' && state.slideType !== 'ANSWER')}
              title="Reveal the answer on the projector — requires the admin PIN"
            >
              Reveal on projector
              <span className="dock-reveal-sub">🔒 admin PIN</span>
            </button>
            <button className="dock-btn dock-standings" onClick={showStandings} title="Show standings (S)">
              Standings · S
            </button>
          </div>

          {/* Phone answer rounds (PHONE or MIXED nights only; both hide
              themselves on paper nights). The big toggle closes or reopens
              the current round in one click. The panel below covers every
              round. Both use the authenticated REST lock route. */}
          <CurrentRoundLockButton
            state={roundLocks}
            roundNumber={currentRound && state.slideType !== 'WELCOME' ? currentRound.orderIndex + 1 : null}
          />
          <div className="host-round-locks">
            <RoundLocksView state={roundLocks} current={currentRound ? currentRound.orderIndex + 1 : null} compact />
          </div>
        </div>
      </div>

      {/* Round Sheet modal — full read-out of the current round's
          questions + answers + notes so the host can announce them
          without looking at the projector. Round-switcher at the top
          lets the host page ahead to next round if they want a
          preview. Current question is highlighted. */}
      {showRoundSheet && (() => {
        const activeRoundIdx = roundSheetRound ?? state.currentRound;
        const r = rounds[activeRoundIdx];
        return (
          <div className="round-drawer-scrim" onClick={() => setShowRoundSheet(false)}>
            <div
              className="round-drawer"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="round-sheet-header">
                <div className="round-sheet-title">
                  <span className="round-sheet-overline">Round Sheet</span>
                  <h2>
                    Round {r ? r.orderIndex + 1 : '?'}
                    {r?.title ? ` — ${r.title}` : ''}
                  </h2>
                </div>
                <div className="round-sheet-nav">
                  {privacyMode && (
                    <Button
                      size="2"
                      variant="soft"
                      color="amber"
                      type="button"
                      onMouseDown={startPeek}
                      onMouseUp={endPeek}
                      onMouseLeave={endPeek}
                      title="Hold to reveal answers on your screen only"
                    >
                      Hold to peek
                    </Button>
                  )}
                  <Button
                    size="2"
                    variant="soft"
                    color="gray"
                    type="button"
                    onClick={() => setRoundSheetRound(Math.max(0, activeRoundIdx - 1))}
                    disabled={activeRoundIdx === 0}
                  >
                    ← Prev
                  </Button>
                  <Button
                    size="2"
                    variant="soft"
                    color="gray"
                    type="button"
                    onClick={() => setRoundSheetRound(Math.min(rounds.length - 1, activeRoundIdx + 1))}
                    disabled={activeRoundIdx >= rounds.length - 1}
                  >
                    Next →
                  </Button>
                  <Button
                    size="2"
                    variant="soft"
                    color="gray"
                    type="button"
                    onClick={() => setShowRoundSheet(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
              <div className="round-sheet-body">
                {!r || r.questions.length === 0 ? (
                  <p className="round-sheet-empty">No questions in this round.</p>
                ) : (
                  <ol className={`round-sheet-list ${privacyMode && !peeking ? 'sheet-private' : ''}`}>
                    {r.questions.map((q, qi) => {
                      const isCurrent =
                        activeRoundIdx === state.currentRound &&
                        qi === state.currentQuestion;
                      return (
                        <li
                          key={q.id}
                          className={`round-sheet-item ${isCurrent ? 'current' : ''}`}
                        >
                          <div className="round-sheet-q-row">
                            <span className="round-sheet-q-num">Q{q.orderIndex + 1}</span>
                            {q.type === 'SONG' && <span className="round-sheet-tag">♪ Song</span>}
                            {q.type === 'IMAGE' && <span className="round-sheet-tag">🖼 Visual</span>}
                          </div>
                          {q.type === 'SONG' ? (
                            <>
                              <div className="round-sheet-q-text">
                                {q.text || '(Song bonus)'}
                              </div>
                              <div className="round-sheet-answer">
                                <span className="round-sheet-answer-label">Answer</span>
                                <span className="round-sheet-answer-value">
                                  {songAnswerText(q) || '—'}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="round-sheet-q-text">{q.text}</div>
                              <div className="round-sheet-answer">
                                <span className="round-sheet-answer-label">Answer</span>
                                <span className="round-sheet-answer-value">{q.answer || '—'}</span>
                                {q.answerPartB && (
                                  <span className="round-sheet-answer-value">/ {q.answerPartB}</span>
                                )}
                              </div>
                            </>
                          )}
                          {q.notes && (
                            <div className="round-sheet-notes">
                              <span className="round-sheet-answer-label">Notes</span>
                              <span>{q.notes}</span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
    </Theme>
  );
};

export default HostPanel;
